import { useState, type FormEvent } from 'react';
import { api } from '../api/client.js';
import { CheckIcon, LockIcon, MailIcon, UserIcon } from '../components/icons.js';
import { useT, type Translate } from '../i18n/index.js';
import { useAccount } from '../session/useAccount.js';

interface LoginScreenProps {
  onBack: () => void;
  onDone: () => void;
}

type Mode = 'login' | 'register';

/**
 * Turns the error code the server sends into the message key that fits it.
 * Registration/login validate the username, email, and password
 * independently server-side, so the server tells us which one actually
 * failed (`invalid_username`/`invalid_email`/`invalid_password`) instead of
 * a single generic code we'd have to guess at — client-side `type="email"`
 * or `pattern` checks are looser than the server's and can let a request
 * through that still gets rejected there.
 */
function messageFor(t: Translate, code: string): string {
  switch (code) {
    case 'invalid_credentials':
      return t('auth.errorCredentials');
    case 'duplicate_email':
      return t('auth.errorDuplicate');
    case 'duplicate_username':
      return t('auth.errorDuplicateUsername');
    case 'invalid_password':
      return t('auth.errorPasswordShort');
    case 'invalid_email':
      return t('auth.errorEmailInvalid');
    case 'invalid_username':
      return t('auth.errorUsernameInvalid');
    case 'too_many_attempts':
      return t('auth.errorTooMany');
    default:
      return t('auth.errorGeneric');
  }
}

/**
 * Signing in only matters for two things: contributing to the Filmwerkstatt
 * and carrying ad-free credit across devices. Everything else — swiping,
 * suggesting a film, joining a session — already works without an account,
 * so this screen has to say that up front rather than let people assume
 * they are locked out until they register.
 */
export function LoginScreen({ onBack, onDone }: LoginScreenProps) {
  const t = useT();
  const account = useAccount();
  const [mode, setMode] = useState<Mode>('login');
  // Logging in accepts either the email or the username in one field, since
  // `accountService.login` tells them apart on its own. Registering needs
  // both at once, so it gets a field of its own for each.
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // What "forgot password" mails to — the real address either way, just read
  // from whichever field currently holds it.
  const forgotTarget = mode === 'login' ? identifier : email;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setForgotSent(false);
    const ok =
      mode === 'login'
        ? await account.login(identifier, password)
        : await account.register(username, email, password);
    setBusy(false);
    if (ok) onDone();
  };

  const forgotPassword = async () => {
    setForgotBusy(true);
    try {
      // Resetting always needs the real address — a username has no mailbox
      // behind it — but this call answers the same way either way (see the
      // catch below), so a username typed here just quietly does nothing.
      await api.forgotPassword(forgotTarget);
    } catch {
      // The endpoint answers the same way whether or not the address is
      // registered — telling people otherwise would turn a password reset
      // form into a way to check who has an account here.
    } finally {
      setForgotBusy(false);
      setForgotSent(true);
    }
  };

  return (
    <form className="screen" onSubmit={submit}>
      {/* Same header as every other screen: the title on the left, the way
          back on the right. */}
      <div className="stack stack--tight">
        <div className="row row--between">
          <h1>{t('auth.title')}</h1>
          <button type="button" className="btn btn--ghost" onClick={onBack}>
            {t('common.back')}
          </button>
        </div>
        <p className="muted">{t('auth.subtitle')}</p>
      </div>

      <div className="auth__switch" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'login'}
          className={`auth__switch-btn${mode === 'login' ? ' auth__switch-btn--active' : ''}`}
          onClick={() => {
            setMode('login');
            account.clearError();
          }}
        >
          {t('auth.login')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'register'}
          className={`auth__switch-btn${mode === 'register' ? ' auth__switch-btn--active' : ''}`}
          onClick={() => {
            setMode('register');
            account.clearError();
          }}
        >
          {t('auth.register')}
        </button>
      </div>

      {mode === 'register' ? (
        <>
          <div className="field">
            <label className="field__label" htmlFor="auth-username">
              {t('auth.username')}
            </label>
            <div className="auth__input-wrap">
              <UserIcon className="auth__input-icon" size={18} />
              <input
                id="auth-username"
                className="input auth__input"
                type="text"
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  account.clearError();
                }}
                autoComplete="username"
                minLength={3}
                maxLength={32}
                // Mirrors `usernameSchema` on the server, which lowercases
                // before checking — so accepting either case here costs
                // nothing and saves a rejection for typing a capital letter.
                pattern="[A-Za-z0-9_-]{3,32}"
                title={t('auth.usernameHint')}
                required
              />
            </div>
            <p className="muted small">{t('auth.usernameHint')}</p>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="auth-email">
              {t('auth.email')}
            </label>
            <div className="auth__input-wrap">
              <MailIcon className="auth__input-icon" size={18} />
              <input
                id="auth-email"
                className="input auth__input"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  account.clearError();
                }}
                autoComplete="email"
                required
              />
            </div>
          </div>
        </>
      ) : (
        <div className="field">
          <label className="field__label" htmlFor="auth-identifier">
            {t('auth.emailOrUsername')}
          </label>
          <div className="auth__input-wrap">
            <MailIcon className="auth__input-icon" size={18} />
            <input
              id="auth-identifier"
              className="input auth__input"
              type="text"
              value={identifier}
              onChange={(event) => {
                setIdentifier(event.target.value);
                account.clearError();
              }}
              autoComplete="username"
              required
            />
          </div>
        </div>
      )}

      <div className="field">
        <label className="field__label" htmlFor="auth-password">
          {t('auth.password')}
        </label>
        <div className="auth__input-wrap">
          <LockIcon className="auth__input-icon" size={18} />
          <input
            id="auth-password"
            className="input auth__input"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              account.clearError();
            }}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            minLength={10}
            required
          />
        </div>
        {/* Said up front rather than by the browser's validation bubble after
            the first attempt. */}
        {mode === 'register' ? <p className="muted small">{t('auth.passwordHint')}</p> : null}
      </div>

      {account.error ? (
        <p className="banner banner--error">{messageFor(t, account.error.code)}</p>
      ) : null}

      <button
        type="submit"
        className="btn btn--primary btn--lg btn--block"
        disabled={
          busy ||
          password.length === 0 ||
          (mode === 'login'
            ? identifier.trim().length === 0
            : username.trim().length === 0 || email.trim().length === 0)
        }
      >
        {busy
          ? t('auth.busy')
          : mode === 'login'
            ? t('auth.submitLogin')
            : t('auth.submitRegister')}
      </button>

      {/* Only where there is a password to have forgotten. */}
      {mode === 'login' ? (
        <div className="row row--center">
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => void forgotPassword()}
            disabled={forgotBusy || forgotTarget.trim().length === 0}
          >
            {forgotSent ? t('auth.forgotSent') : t('auth.forgot')}
          </button>
        </div>
      ) : null}

      <div className="auth__divider" />

      <div className="panel stack">
        <h2 className="auth__carries-title">{t('auth.carriesOver')}</h2>
        <div className="stack stack--tight">
          <div className="row auth__carries-item">
            <CheckIcon className="auth__check-icon" size={17} strokeWidth={2.2} />
            <span>{t('auth.carriesWatchlist')}</span>
          </div>
          <div className="row auth__carries-item">
            <CheckIcon className="auth__check-icon" size={17} strokeWidth={2.2} />
            <span>{t('auth.carriesRatings')}</span>
          </div>
          <div className="row auth__carries-item">
            <CheckIcon className="auth__check-icon" size={17} strokeWidth={2.2} />
            <span>{t('auth.carriesFilms')}</span>
          </div>
        </div>
        <p className="muted small">{t('auth.carriesNote')}</p>
      </div>
    </form>
  );
}
